import type { AuspiceJsonV2 } from 'auspice'
import { concurrent } from 'fasy'
import { isEmpty, isNil, omitBy } from 'lodash'
import pProps from 'p-props'
import { useRouter } from 'next/router'
import { useRecoilCallback } from 'recoil'
import { useSetAtom } from 'jotai'
import { useRef } from 'react'
import { REF_NODE_ROOT } from 'src/constants'
import { resolveRefNodeIds } from 'src/helpers/refNodeDropdown'
import { ErrorInternal } from 'src/helpers/ErrorInternal'
import type { DatasetAssignment } from 'src/helpers/datasetAssignment'
import {
  seqIndexToTopDatasetNameAtom,
  seqIndicesWithoutDatasetSuggestionsAtom,
  topSuggestedDatasetsAtom,
} from 'src/state/autodetect.state'
import { clearAllFiltersAtom } from 'src/state/resultFilters.state'
import { allViewedCdsAtom, viewedCdsAtom } from 'src/state/seqViewSettings.state'
import {
  AlgorithmGlobalStatus,
  AlgorithmInput,
  AuspiceTree,
  Dataset,
  NextcladeParamsRaw,
  NextcladeParamsRawDir,
  NextcladeResult,
} from 'src/types'
import { sanitizeError } from 'src/helpers/sanitizeError'
import {
  cdsOrderPreferenceAtom,
  allCdsOrderPreferenceAtom,
  datasetAssignmentForAnalysisAtom,
  datasetSingleCurrentAtom,
  viewedDatasetNameAtom,
} from 'src/state/dataset.state'
import { globalErrorAtom } from 'src/state/error.state'
import {
  datasetJsonAtom,
  geneMapInputAtom,
  qrySeqInputsStorageAtom,
  refSeqInputAtom,
  refTreeInputAtom,
  virusPropertiesInputAtom,
} from 'src/state/inputs.state'
import {
  aaMotifsDescsAtom,
  allGenomeSizesAtom,
  allGenesAtom,
  allCdsesAtom,
  allTreesAtom,
  allTreesNwkAtom,
  allCladeNodeAttrDescsAtom,
  allPhenotypeAttrDescsAtom,
  allRefNodesAtom,
  allAaMotifsDescsAtom,
  analysisResultsAtom,
  analysisStatusGlobalAtom,
  cdsesAtom,
  cladeNodeAttrDescsAtom,
  csvColumnConfigAtom,
  currentRefNodeNameAtom,
  genesAtom,
  genomeSizeAtom,
  phenotypeAttrDescsAtom,
  refNodesAtom,
  treeAtom,
  treeNwkAtom,
  allCurrentRefNodeNameAtom,
  initialDataAtom,
  allInitialDataAtom,
  mutationPatternKeysAtom,
} from 'src/state/results.state'
import { numThreadsAtom } from 'src/state/settings.state'
import { launchAnalysis, LaunchAnalysisCallbacks, DatasetFilesOverrides } from 'src/workers/launchAnalysis'
import { axiosFetchRaw, axiosFetchRawMaybe } from 'src/io/axiosFetch'

export function useRunAnalysis() {
  const router = useRouter()

  const setAnalysisStatusGlobal = useSetAtom(analysisStatusGlobalAtom)
  const setAnalysisResults = useSetAtom(analysisResultsAtom)
  const setClearAllFilters = useSetAtom(clearAllFiltersAtom)

  // Store results temporarily to batch update them
  const resultsRef = useRef<NextcladeResult[]>([])

  return useRecoilCallback(
    ({ set, reset, snapshot }) =>
      ({ isSingle }: { isSingle: boolean }) => {
        setAnalysisStatusGlobal(AlgorithmGlobalStatus.loadingData)

        setAnalysisResults([])
        setClearAllFilters()
        resultsRef.current = []

        reset(allAaMotifsDescsAtom)
        reset(allCdsOrderPreferenceAtom)
        reset(allCdsesAtom)
        reset(allCladeNodeAttrDescsAtom)
        reset(allCurrentRefNodeNameAtom)
        reset(allGenesAtom)
        reset(allGenomeSizesAtom)
        reset(allInitialDataAtom)
        reset(allPhenotypeAttrDescsAtom)
        reset(allRefNodesAtom)
        reset(allTreesAtom)
        reset(allTreesNwkAtom)
        reset(allViewedCdsAtom)

        const callbacks: LaunchAnalysisCallbacks = {
          onGlobalStatus(status) {
            setAnalysisStatusGlobal(status)
          },
          onInitialData(datasetName, initialData) {
            set(initialDataAtom(datasetName), initialData)

            const {
              geneMap,
              genomeSize,
              defaultCds,
              cdsOrderPreference,
              cladeNodeAttrKeyDescs,
              phenotypeAttrDescs,
              refNodes,
              aaMotifsDescs,
              mutationPatternKeys,
              csvColumnConfigDefault,
            } = initialData

            const genes = Object.values(geneMap.genes)
            set(genesAtom({ datasetName }), genes)

            const cdses = Object.values(geneMap.genes).flatMap((gene) => gene.cdses)
            set(cdsesAtom({ datasetName }), cdses)
            set(genomeSizeAtom({ datasetName }), genomeSize)

            if (defaultCds) {
              set(viewedCdsAtom({ datasetName }), defaultCds)
            }

            if (cdsOrderPreference) {
              set(cdsOrderPreferenceAtom({ datasetName }), cdsOrderPreference)
            }

            set(cladeNodeAttrDescsAtom({ datasetName }), cladeNodeAttrKeyDescs)
            set(phenotypeAttrDescsAtom({ datasetName }), phenotypeAttrDescs)
            set(refNodesAtom({ datasetName }), refNodes)

            // Preselect the "Relative to" entry among the resolved visible entries (order/hide aware): the
            // configured `default` if it is still visible, else the reference sequence if visible, else the
            // first visible entry. `default` stays orthogonal to ordering.
            const visibleRefNodeIds = resolveRefNodeIds(refNodes, cladeNodeAttrKeyDescs)
            const preselectedRefNode =
              !isNil(refNodes.default) && visibleRefNodeIds.includes(refNodes.default)
                ? refNodes.default
                : (visibleRefNodeIds.find((id) => id === REF_NODE_ROOT) ?? visibleRefNodeIds[0] ?? REF_NODE_ROOT)
            set(currentRefNodeNameAtom({ datasetName }), preselectedRefNode)

            set(aaMotifsDescsAtom({ datasetName }), aaMotifsDescs)
            set(mutationPatternKeysAtom({ datasetName }), mutationPatternKeys)
            set(csvColumnConfigAtom, csvColumnConfigDefault)
          },
          onAnalysisResult(result) {
            // Store the result in the ref and update Jotai atom
            resultsRef.current.push(result)
            setAnalysisResults([...resultsRef.current])
          },
          onError(error) {
            set(globalErrorAtom, error)
          },
          onTree(trees) {
            Object.entries(trees).forEach(([datasetName, trees]) => {
              set(treeAtom(datasetName), (trees?.auspice as unknown as AuspiceJsonV2) ?? undefined)
              set(treeNwkAtom({ datasetName }), trees?.nwk ?? undefined)
            })
          },
          onComplete() {},
        }

        const releaseSnapshot = snapshot.retain()
        const { getPromise } = snapshot

        router
          .push('/results', '/results')
          .then(async () => {
            setAnalysisStatusGlobal(AlgorithmGlobalStatus.initWorkers)
            const numThreadsResolved = await getPromise(numThreadsAtom)
            const qry = await getPromise(qrySeqInputsStorageAtom)
            const csvColumnConfig = await getPromise(csvColumnConfigAtom)
            const datasetSingleCurrent = await getPromise(datasetSingleCurrentAtom)
            const tree = await getPromise(datasetJsonAtom)

            const overrides: DatasetFilesOverrides = {
              reference: getPromise(refSeqInputAtom),
              genomeAnnotation: getPromise(geneMapInputAtom),
              treeJson: getPromise(refTreeInputAtom),
              pathogenJson: getPromise(virusPropertiesInputAtom),
            }

            const topSuggestedDatasets = await getPromise(topSuggestedDatasetsAtom)
            const seqIndexToTopDatasetName = await getPromise(seqIndexToTopDatasetNameAtom)
            const seqIndicesWithoutDatasetSuggestions = await getPromise(seqIndicesWithoutDatasetSuggestionsAtom)

            const datasets = findDatasets(isSingle, datasetSingleCurrent, topSuggestedDatasets)
            const datasetNames = datasets.map((dataset) => dataset.path)
            const assignment: DatasetAssignment = {
              datasetNames,
              seqIndexToDatasetName: !isSingle ? seqIndexToTopDatasetName : undefined,
              seqIndicesWithoutDataset: !isSingle ? new Set(seqIndicesWithoutDatasetSuggestions) : undefined,
            }
            set(datasetAssignmentForAnalysisAtom, assignment)
            if (!isNil(datasetNames[0])) {
              set(viewedDatasetNameAtom, datasetNames[0])
            }
            const params: NextcladeParamsRaw = await resolveParams(tree, overrides, datasets)
            return launchAnalysis(
              assignment,
              qry,
              params,
              callbacks,
              numThreadsResolved,
              csvColumnConfig,
            )
          })
          .finally(() => {
            releaseSnapshot()
          })
          .catch((error) => {
            setAnalysisStatusGlobal(AlgorithmGlobalStatus.failed)
            set(globalErrorAtom, sanitizeError(error))
          })
      },
    [router, setAnalysisResults, setAnalysisStatusGlobal, setClearAllFilters],
  )
}

function findDatasets(
  isSingleDatasetMode: boolean,
  datasetSingleCurrent: Dataset | undefined,
  topSuggestedDatasets: Dataset[] | undefined,
) {
  if (isSingleDatasetMode) {
    if (isNil(datasetSingleCurrent)) {
      throw new ErrorInternal(`Attempted to start analysis without a dataset selected (single dataset mode)`)
    }
    return [datasetSingleCurrent]
  }
  if (isNil(topSuggestedDatasets) || isEmpty(topSuggestedDatasets)) {
    throw new ErrorInternal(`Attempted to start analysis without datasets selected (multi-dataset mode)`)
  }
  return topSuggestedDatasets
}

async function resolveParams(
  tree: AuspiceTree | undefined,
  overrides: DatasetFilesOverrides,
  datasets: Dataset[] | undefined,
): Promise<NextcladeParamsRaw> {
  if (tree) {
    if (!datasets?.[0]) {
      throw new ErrorInternal('Attempted to start analysis with Auspice JSON, but without dataset info resolved')
    }
    return resolveInputsForAuspiceDataset(tree, overrides, datasets[0].path)
  }
  return resolveInputsForNextcladeDataset(overrides, datasets)
}

async function resolveInputsForAuspiceDataset(
  tree: AuspiceTree | undefined,
  overrides: DatasetFilesOverrides,
  datasetName: string,
): Promise<NextcladeParamsRaw> {
  const resolvedOverrides = await pProps({
    genomeAnnotation: resolveOverride(overrides.genomeAnnotation),
    reference: resolveOverride(overrides.reference),
    treeJson: resolveOverride(overrides.treeJson),
    pathogenJson: resolveOverride(overrides.pathogenJson),
  })
  const filteredOverrides = omitBy(resolvedOverrides, isNil)
  return {
    Auspice: {
      auspiceJson: JSON.stringify(tree),
      ...filteredOverrides,
      datasetName,
    },
  }
}

async function resolveInputsForNextcladeDataset(overrides: DatasetFilesOverrides, datasets: Dataset[] | undefined) {
  if (isNil(datasets) || isEmpty(datasets)) {
    throw new ErrorInternal('Dataset is required but not found')
  }
  const datasetsFiles = await getDatasetsFiles(overrides, datasets)
  return { Dir: datasetsFiles }
}

async function getDatasetsFiles(
  overrides: DatasetFilesOverrides,
  datasets: Dataset[],
): Promise<NextcladeParamsRawDir[]> {
  // TODO: We can override files only if there's 1 dataset. Consider implementing multi-dataset overrides
  if (datasets?.length === 1) {
    return [await getDatasetFilesWithOverrides(overrides, datasets[0])]
  }
  return getDatasetFiles(datasets)
}

/** Resolves all dataset files into strings */
async function getDatasetFiles(datasets: Dataset[]): Promise<NextcladeParamsRawDir[]> {
  return concurrent.map(async (dataset) => {
    return {
      datasetName: dataset.path,
      genomeAnnotation: await axiosFetchRawMaybe(dataset.files?.genomeAnnotation),
      reference: await axiosFetchRaw(dataset.files?.reference),
      treeJson: await axiosFetchRawMaybe(dataset.files?.treeJson),
      pathogenJson: await axiosFetchRaw(dataset.files?.pathogenJson),
    }
  }, datasets)
}

/** Resolves all dataset files overrides into strings, substitutes missing files from dataset files */
async function getDatasetFilesWithOverrides(
  overrides: DatasetFilesOverrides,
  dataset: Dataset,
): Promise<NextcladeParamsRawDir> {
  return {
    datasetName: dataset.path,
    genomeAnnotation: await resolveOverrideOrDatasetFile(overrides.genomeAnnotation, dataset.files?.genomeAnnotation),
    reference: await resolveOverrideOrDatasetFileRequired(overrides.reference, dataset.files?.reference),
    treeJson: await resolveOverrideOrDatasetFile(overrides.treeJson, dataset.files?.treeJson),
    pathogenJson: await resolveOverrideOrDatasetFileRequired(overrides.pathogenJson, dataset.files?.pathogenJson),
  }
}

async function resolveOverrideOrDatasetFileRequired(
  override: Promise<AlgorithmInput | undefined>,
  datasetFileUrl: string | undefined,
): Promise<string> {
  const data = await resolveOverrideOrDatasetFile(override, datasetFileUrl)
  if (!data) {
    throw new Error('Unable to resolve dataset file')
  }
  return data
}

async function resolveOverrideOrDatasetFile(
  override: Promise<AlgorithmInput | undefined>,
  datasetFileUrl: string | undefined,
): Promise<string | undefined> {
  // If override is provided load it
  const overrideResolved = await resolveOverride(override)
  if (overrideResolved) {
    return overrideResolved
  }

  // Otherwise fetch corresponding file from the dataset
  if (datasetFileUrl) {
    return axiosFetchRaw(datasetFileUrl)
  }

  return undefined
}

async function resolveOverride(override: Promise<AlgorithmInput | undefined>) {
  const awaitedInput = await override
  return awaitedInput?.getContent()
}
